// PaymentReceiverCard — what the player sees when they're about to
// pay (or have just been told to pay). Shows the receiver's UPI ID
// with a one-tap copy button, the QR image (tappable to fullscreen
// for easy scanning), the WhatsApp deep-link, and a support phone
// number. When the receiver hasn't configured anything yet, falls
// back gracefully to a "Contact us" card so the player still has a
// path forward instead of a dead end.
//
// Used by RechargeFlow.tsx step 2 and embeddable elsewhere if we
// add more "show payment info" surfaces later.
//
// Phase 19: "Open UPI app" no longer fires upi:// blindly and lets
// the OS pick — that was inconsistent across Android versions and
// completely broken on iOS Safari. Now it pops a chooser with the
// four major Indian UPI apps (GPay / PhonePe / Paytm / BHIM) plus
// an "Other UPI app" escape hatch. Each named app uses the Android
// intent:// form with an explicit package= so the right app opens
// directly; the escape hatch falls back to plain upi://. Logos are
// drag-and-droppable into player-frontend/public/upi-logos/ — if a
// logo file is missing, the chooser shows a letter-avatar fallback,
// so the flow keeps working even before the user installs icons.

import { useState } from 'react';
import Icon from './Icon';
import { useT } from '../lib/i18n';
import { useToast } from '../lib/toast';

export type PaymentConfig = {
  target_admin_id: number;
  upi_id: string | null;
  qr_image_url: string | null;
  whatsapp_number: string | null;
  support_phone: string | null;
  display_note: string | null;
  is_active: 0 | 1;
  configured: boolean;
  // Cascade Recharge Lock — Dynamic Capacity (Phase R7 follow-up,
  // 2026-05-14). See depositGuards.js banner for the full story.
  //
  // parent_locked / lock_reason — server flips parent_locked=true
  // when the user's immediate parent is fully out of capacity OR
  // capacity is positive but below the player MIN_AMOUNT. In that
  // case ALL payment-reachable fields above are null and the
  // recharge UI swaps to RechargeLockedPanel.
  //
  // remaining_capacity — when the parent's wallet is negative but
  // there's still enough cushion left for a recharge above MIN, the
  // server returns the FULL payment config PLUS this cap. The UI
  // must:
  //   1. Hide quick-pick chips whose value > remaining_capacity.
  //   2. Clamp the manual amount input at remaining_capacity.
  //   3. Show a "Max available right now: ₹{n}" banner.
  // When null (parent healthy: wallet >= 0), no cap applies and the
  // UI uses its normal MAX_AMOUNT.
  parent_locked?: boolean;
  /**
   *  'temporary_recharge_outage' — Cascade Recharge Lock (parent
   *    breached or out of capacity). Calm panel says "we are
   *    resolving a problem with this recharge route."
   *  'not_configured' — Phase R7 follow-up (2026-05-14). Parent
   *    has not yet activated their player-facing QR. Calm panel
   *    swaps to "recharge will be available soon, finalising setup"
   *    — a less-alarming variant because nothing is wrong, setup
   *    is just incomplete.
   */
  lock_reason?: 'temporary_recharge_outage' | 'not_configured' | null;
  remaining_capacity?: number | null;
};

// One row in the chooser.
//   - `androidPkg` is the package id used to build an intent:// URL
//     that targets that exact app on Android.
//   - `iosScheme` is the iOS-side custom URL scheme registered by
//     each app; iOS Safari does NOT understand intent:// (the player
//     gets "Safari cannot open the page because the address is
//     invalid"), so on iOS we send `<scheme>://upi/pay?...` instead.
//   - `letter` is the fallback initial shown if the logo image fails
//     to load.
type UpiApp = {
  id: 'gpay' | 'phonepe' | 'paytm' | 'bhim' | 'other';
  name: string;
  logoSrc: string | null;
  androidPkg: string | null;
  iosScheme: string | null;
  letter: string;
  // Each well-known app gets a brand tint so the avatar fallback
  // (and the icon disc behind the logo) is still recognizable
  // before custom icons are dropped in.
  tint: string;
};

const UPI_APPS: UpiApp[] = [
  // iOS schemes per each app's documented integration:
  //   gpay      → gpay://upi/pay?…  (legacy tez:// also works)
  //   phonepe   → phonepe://upi/pay?…
  //   paytm     → paytmmp://upi/pay?…  (note: NOT paytm://)
  //   bhim      → bhim://upi/pay?…
  // Verified via the official PhonePe iOS docs and Paytm "UPI Intent
  // on iOS" docs. tez:// and gpay:// are both routed to Google Pay
  // India on iOS.
  { id: 'gpay',    name: 'Google Pay',    logoSrc: '/upi-logos/gpay.png',    androidPkg: 'com.google.android.apps.nbu.paisa.user', iosScheme: 'gpay',    letter: 'G', tint: 'bg-white border-neutral-200' },
  { id: 'phonepe', name: 'PhonePe',       logoSrc: '/upi-logos/phonepe.png', androidPkg: 'com.phonepe.app',                        iosScheme: 'phonepe', letter: 'P', tint: 'bg-[#5f259f] text-white border-[#5f259f]' },
  { id: 'paytm',   name: 'Paytm',         logoSrc: '/upi-logos/paytm.png',   androidPkg: 'net.one97.paytm',                        iosScheme: 'paytmmp', letter: 'P', tint: 'bg-[#00baf2] text-white border-[#00baf2]' },
  { id: 'bhim',    name: 'BHIM',          logoSrc: '/upi-logos/bhim.png',    androidPkg: 'in.org.npci.upiapp',                     iosScheme: 'bhim',    letter: 'B', tint: 'bg-[#ff7022] text-white border-[#ff7022]' },
  { id: 'other',   name: 'Other UPI app', logoSrc: null,                     androidPkg: null,                                     iosScheme: null,      letter: '⋯', tint: 'bg-arena-100 dark:bg-arena-700/60 text-arena-700 dark:text-neon-200 border-arena-300 dark:border-arena-600' },
];

// Detect Android via UA. Anything else (iOS, desktop, in-app
// webviews without an Android UA) goes through the iOS / generic
// upi:// path. We don't try to spoof-detect; the worst case on a
// misdetected device is the chooser opens the OS UPI handler
// instead of pinning to a specific app.
function isAndroid(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /android/i.test(navigator.userAgent);
}

function buildUpiLink(app: UpiApp, qs: string): string {
  // Android: intent:// pinned to the exact package, with Play Store
  // fallback if the app isn't installed. Works everywhere on
  // Android including Chrome's WebView and Samsung Internet.
  if (isAndroid() && app.androidPkg) {
    return `intent://pay?${qs}#Intent;scheme=upi;package=${app.androidPkg};` +
           `S.browser_fallback_url=${encodeURIComponent(`https://play.google.com/store/apps/details?id=${app.androidPkg}`)};` +
           `end`;
  }
  // iOS / desktop / unknown: each app has its own iOS custom URL
  // scheme that pre-fills the UPI payment. intent:// is Android-
  // only and produces "Safari cannot open the page because the
  // address is invalid" on iOS, which is exactly what this branch
  // exists to avoid.
  if (app.iosScheme) {
    return `${app.iosScheme}://upi/pay?${qs}`;
  }
  // "Other UPI app" — no specific scheme. Plain upi:// triggers
  // whichever UPI app the OS has registered as the default
  // handler. On Android any UPI app handles it; on iOS it works
  // only if the user has an app that claimed the upi:// scheme.
  return `upi://pay?${qs}`;
}

export default function PaymentReceiverCard({
  cfg, amount, onPaymentInitiated,
}: {
  cfg: PaymentConfig;
  amount: number | null;
  // 2026-05-21 — fires the moment the player taps a UPI-app deep link
  // (or the WhatsApp / phone fallback when no UPI is configured). The
  // RechargeFlow uses this to flip its "I have paid" gate from
  // hidden/disabled to visible — the player must have actually
  // hopped to a payment surface before they can confirm payment.
  onPaymentInitiated?: () => void;
}) {
  const t = useT();
  const { push } = useToast();
  const [zoomQr, setZoomQr] = useState(false);
  const [showChooser, setShowChooser] = useState(false);

  // Build the various deep links / actions.
  const upiQuery = cfg.upi_id && amount
    ? `pa=${encodeURIComponent(cfg.upi_id)}&am=${amount}&cu=INR&tn=${encodeURIComponent('BaaliGames recharge')}`
    : null;
  const whatsappLink = cfg.whatsapp_number
    ? `https://wa.me/${cfg.whatsapp_number.replace(/\D/g, '')}${amount ? `?text=${encodeURIComponent(`Hi, I want to recharge ₹${amount} on BaaliGames`)}` : ''}`
    : null;

  async function copyUpi() {
    if (!cfg.upi_id) return;
    try {
      await navigator.clipboard.writeText(cfg.upi_id);
      push('success', t('payment.upiCopied'));
    } catch {
      push('info', cfg.upi_id);
    }
  }

  function openInApp(app: UpiApp) {
    if (!upiQuery) return;
    const link = buildUpiLink(app, upiQuery);
    // Use a transient anchor so we get the same handling as a
    // user-initiated link (Safari/iOS is more permissive about
    // popping out of the page when the navigation comes from a
    // real <a> than from window.location).
    const a = document.createElement('a');
    a.href = link;
    a.rel = 'noreferrer';
    a.click();
    setShowChooser(false);
    // Notify the parent flow that the player has hopped out to a UPI
    // app. The flow's "I have paid" gate listens for this signal.
    onPaymentInitiated?.();
  }

  // Fallback view — receiver hasn't configured a UPI. Show whatever
  // contact channel they have so the player can ask for help.
  if (!cfg.configured) {
    const hasFallback = !!(cfg.whatsapp_number || cfg.support_phone);
    return (
      <div className="rounded-2xl border border-amber-300 dark:border-amber-400/30
                      bg-amber-50/80 dark:bg-amber-900/15 p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl grid place-items-center
                          bg-amber-200/50 dark:bg-amber-400/15
                          text-amber-700 dark:text-amber-300">
            <Icon name="warning" size={22} filled />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-extrabold text-base text-amber-900 dark:text-amber-200">
              {t('payment.notConfigured.title')}
            </h3>
            <p className="text-xs text-amber-800/80 dark:text-amber-200/80 mt-0.5">
              {hasFallback ? t('payment.notConfigured.contactBody') : t('payment.notConfigured.unavailable')}
            </p>
          </div>
        </div>
        {hasFallback && (
          <div className="mt-3 flex flex-wrap gap-2">
            {whatsappLink && (
              <a href={whatsappLink} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 h-9 px-3 rounded-full
                            bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold">
                <Icon name="chat" size={16} filled /> WhatsApp
              </a>
            )}
            {cfg.support_phone && (
              <a href={`tel:${cfg.support_phone}`}
                 className="inline-flex items-center gap-1 h-9 px-3 rounded-full
                            bg-arena-700 hover:bg-arena-600 text-white text-xs font-bold">
                <Icon name="call" size={16} filled /> {cfg.support_phone}
              </a>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="rounded-2xl border border-arena-200 dark:border-neon-400/25
                      bg-white/70 dark:bg-arena-800/60 backdrop-blur p-4 space-y-3">
        {/* QR code — large, tappable to fullscreen for scanning */}
        {cfg.qr_image_url && (
          <button
            type="button"
            onClick={() => setZoomQr(true)}
            className="block mx-auto rounded-xl overflow-hidden bg-white p-2 shadow-md
                       hover:shadow-lg active:scale-[.98] transition"
            aria-label={t('payment.qr.tap')}
          >
            <img src={cfg.qr_image_url} alt="" className="w-44 h-44 sm:w-52 sm:h-52 object-contain" />
            <div className="text-[10px] text-arena-500 mt-1 inline-flex items-center gap-1 justify-center w-full">
              <Icon name="zoom_in" size={12} /> {t('payment.qr.tap')}
            </div>
          </button>
        )}

        {/* UPI ID with copy + open-in-app actions */}
        {cfg.upi_id && (
          <div>
            <div className="text-[10px] uppercase tracking-[.18em] font-bold text-arena-500 dark:text-neon-200/60 mb-1">
              {t('payment.upi.label')}
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 font-mono font-bold text-sm break-all
                               bg-arena-100 dark:bg-arena-900/60 border border-arena-200 dark:border-arena-600/40
                               rounded-lg px-3 py-2 text-arena-800 dark:text-neon-100">
                {cfg.upi_id}
              </code>
              <button
                type="button" onClick={copyUpi}
                className="shrink-0 inline-flex items-center gap-1 h-9 px-3 rounded-full
                           bg-arena-100 dark:bg-arena-700/60
                           border border-arena-300 dark:border-arena-600
                           text-arena-700 dark:text-neon-200 text-xs font-bold
                           hover:bg-arena-200 dark:hover:bg-arena-700"
              >
                <Icon name="content_copy" size={14} /> {t('payment.upi.copy')}
              </button>
            </div>
          </div>
        )}

        {/* Quick-action buttons */}
        <div className="flex flex-wrap gap-2">
          {upiQuery && (
            <button
              type="button"
              onClick={() => setShowChooser(true)}
              className="flex-1 min-w-[8rem] inline-flex items-center justify-center gap-1.5 h-11 px-4 rounded-full
                         font-bold text-[12px] tracking-wider uppercase text-white
                         bg-gradient-to-b from-brand-400 via-brand-500 to-brand-600
                         border border-white/15 active:scale-[.98] transition"
              style={{ boxShadow: '0 8px 18px -8px rgba(var(--brand-rgb),.7), inset 0 1px 0 rgba(255,255,255,.3)' }}>
              <Icon name="open_in_new" size={16} filled /> {t('payment.openUpiApp')}
            </button>
          )}
          {whatsappLink && (
            <a href={whatsappLink} target="_blank" rel="noreferrer"
               className="flex-1 min-w-[8rem] inline-flex items-center justify-center gap-1.5 h-11 px-4 rounded-full
                          font-bold text-[12px] tracking-wider uppercase text-white
                          bg-emerald-600 hover:bg-emerald-500 active:scale-[.98] transition">
              <Icon name="chat" size={16} filled /> {t('payment.openWhatsapp')}
            </a>
          )}
        </div>

        {/* Display note from admin */}
        {cfg.display_note && (
          <div className="rounded-lg bg-neon-100/40 dark:bg-neon-400/10
                          border border-neon-300/50 dark:border-neon-400/30
                          px-3 py-2 text-[12px] text-arena-700 dark:text-neon-100">
            <Icon name="info" size={14} filled className="mr-1 align-middle" />
            {cfg.display_note}
          </div>
        )}

        {/* Support phone fallthrough */}
        {cfg.support_phone && (
          <div className="text-[11px] text-center text-arena-500 dark:text-neon-200/60">
            {t('payment.helpAt')} <a href={`tel:${cfg.support_phone}`} className="font-bold text-arena-700 dark:text-neon-300 underline underline-offset-2">{cfg.support_phone}</a>
          </div>
        )}
      </div>

      {/* QR fullscreen zoom — black backdrop, large QR for scanning */}
      {zoomQr && cfg.qr_image_url && (
        <div className="fixed inset-0 z-[80] bg-black/95 grid place-items-center p-4 cursor-pointer"
             onClick={() => setZoomQr(false)}
             role="dialog" aria-modal="true" aria-label={t('payment.qr.label')}>
          <img src={cfg.qr_image_url} alt=""
               className="max-w-full max-h-[80vh] rounded-xl bg-white p-4" />
          <button
            onClick={() => setZoomQr(false)}
            aria-label={t('common.close')}
            className="absolute top-4 right-4 w-10 h-10 rounded-full grid place-items-center
                       bg-white/10 hover:bg-white/20 text-white">
            <Icon name="close" size={22} />
          </button>
          <div className="absolute bottom-6 left-0 right-0 text-center text-white/80 text-sm">
            {t('payment.qr.tapToClose')}
          </div>
        </div>
      )}

      {/* UPI app chooser — appears between "Open UPI app" tap and the
          actual deep-link, so the player gets to pick which UPI app
          to hand off to instead of relying on the OS picker.

          Visual language matches RechargeFlow exactly: same backdrop,
          same rounded-3xl card, same neon-400/25 border on dark, same
          gradient brand header disc, same slide-down entrance. The
          chooser feels like a continuation of the same wizard, not a
          separate dialog. */}
      {showChooser && upiQuery && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-2 sm:p-4
                     bg-black/60 backdrop-blur-sm animate-slide-down"
          role="dialog" aria-modal="true" aria-label={t('payment.chooser.title')}
          onClick={() => setShowChooser(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md flex flex-col rounded-3xl overflow-hidden
                       bg-white dark:bg-arena-800
                       border border-arena-200 dark:border-neon-400/25 shadow-2xl"
          >
            {/* Header — mirrors RechargeFlow's header so the chooser
                reads as the same UI surface. */}
            <div className="px-4 pt-4 pb-3 border-b border-arena-200 dark:border-arena-600/40 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl grid place-items-center
                                bg-gradient-to-br from-brand-300/40 to-brand-500/30
                                text-brand-700 dark:text-brand-300">
                  <Icon name="account_balance_wallet" size={22} filled />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-display font-extrabold text-base text-arena-800 dark:text-neon-100 leading-tight">
                    {t('payment.chooser.title')}
                  </h2>
                  {amount && (
                    <p className="text-[11px] text-arena-500 dark:text-neon-200/70 leading-tight">
                      {t('payment.chooser.subtitle')}{' '}
                      <b className="text-arena-800 dark:text-neon-100">₹{amount.toLocaleString('en-IN')}</b>
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setShowChooser(false)}
                  aria-label={t('common.close')}
                  className="shrink-0 w-9 h-9 rounded-full grid place-items-center
                             text-arena-500 dark:text-neon-200/70
                             hover:bg-arena-100 dark:hover:bg-arena-700/60"
                >
                  <Icon name="close" size={20} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-4 py-4 space-y-3">
              <div className="grid grid-cols-2 gap-2.5">
                {UPI_APPS.map((app) => (
                  <button
                    key={app.id}
                    type="button"
                    onClick={() => openInApp(app)}
                    className="flex flex-col items-center justify-center gap-2 p-3 rounded-2xl
                               border border-arena-200 dark:border-arena-600/40
                               bg-white dark:bg-arena-900/40
                               hover:border-brand-400 dark:hover:border-brand-400/70
                               hover:bg-brand-50/40 dark:hover:bg-brand-900/15
                               active:scale-[.97] transition shadow-sm hover:shadow-md"
                  >
                    {/* Logo disc. The letter sits behind the <img> as
                        a CSS placeholder so if the icon file isn't
                        dropped in yet, the chooser still renders a
                        recognizable single-letter avatar in the brand
                        tint instead of a broken image. */}
                    <span className={`relative w-12 h-12 rounded-xl grid place-items-center overflow-hidden
                                      border ${app.tint} shadow-inner`}>
                      <span className="font-display font-extrabold text-xl select-none">
                        {app.letter}
                      </span>
                      {app.logoSrc && (
                        <img
                          src={app.logoSrc}
                          alt=""
                          className="absolute inset-0 w-full h-full object-contain bg-white"
                          onError={(e) => {
                            // Drop the broken <img> so the letter
                            // avatar underneath becomes visible.
                            (e.currentTarget as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                    </span>
                    <span className="text-[12px] font-bold text-arena-800 dark:text-neon-100 text-center leading-tight">
                      {app.name}
                    </span>
                  </button>
                ))}
              </div>

              <p className="text-[10.5px] text-arena-500 dark:text-neon-200/60 leading-relaxed pt-1
                            border-t border-arena-200 dark:border-arena-600/40 pt-3 mt-1">
                <Icon name="info" size={12} className="inline align-middle mr-1" />
                {t('payment.chooser.hint')}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
